import { httpsCallable } from 'firebase/functions';
import { showVentaLogradaCelebracion } from '../../../core/ui/ventaLogradaCelebracion.js';
import { progresoSiguienteNivel } from '../domain/vendedorNivelRules.js';
import { mlvApplyZ } from '../../../core/ui/mlvZLayers.js';
import { mlvSaveFocus } from '../../../core/ui/mlvFocusRestore.js';
import { mlvModalHistoryAttach } from '../../../core/navigation/mlvModalHistoryBridge.js';
import { mlvTrack } from '../../../core/telemetry/mlvTrack.js';
import { MLV_EVENTS } from '../../../core/telemetry/mlvEvents.js';

export function openCompradorTagModal() {
  return new Promise((resolve) => {
    const restoreFocus = mlvSaveFocus();
    const prev = document.getElementById('mlv-comprador-tag-modal');
    if (prev) prev.remove();
    const wrap = document.createElement('div');
    wrap.id = 'mlv-comprador-tag-modal';
    wrap.className =
      'fixed inset-0 flex items-end justify-center bg-black/55 p-4 sm:items-center';
    mlvApplyZ(wrap, 'compradorTag');
    wrap.innerHTML = `
      <div class="w-full max-w-md rounded-t-3xl bg-white p-5 shadow-2xl ring-1 ring-slate-200/80 sm:rounded-3xl">
        <h3 class="font-heading text-lg font-bold text-slate-900">¿Te pasó el código del comprador? <span class="text-sm font-semibold text-slate-500">(opcional)</span></h3>
        <p class="mt-2 text-sm leading-relaxed text-slate-600">
          Si en WhatsApp te mandó su <strong class="font-semibold text-slate-800">código de 4 letras o números</strong>, pegalo acá: así le llega el aviso para <strong class="font-semibold text-slate-800">calificarte</strong>. Si no lo tenés, no pasa nada.
        </p>
        <input id="mlv-comprador-tag-input" type="text" maxlength="128" autocomplete="off" class="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-primary/30" placeholder="Ej: K7MN" />
        <p class="mt-2 text-[11px] leading-relaxed text-slate-500">
          La venta y tu nivel se actualizan igual; el código solo habilita la reseña para el comprador.
        </p>
        <div class="mt-5 flex gap-2">
          <button type="button" id="mlv-tag-cancel" class="flex-1 rounded-full border border-slate-300 bg-white py-2.5 text-sm font-semibold text-slate-700">Omitir</button>
          <button type="button" id="mlv-tag-ok" class="flex-1 rounded-full bg-brand-primary py-2.5 text-sm font-semibold text-white">Guardar</button>
        </div>
      </div>
    `;
    let pendingTag = '';
    const hist = mlvModalHistoryAttach(() => {
      document.removeEventListener('keydown', onKey);
      try {
        restoreFocus();
      } catch (_e) {
        /* ignore */
      }
      wrap.remove();
      resolve(pendingTag);
    });
    function closeAndResolve(v) {
      pendingTag = String(v || '').trim();
      hist.closeFromUi();
    }
    function onKey(e) {
      if (e.key === 'Escape') closeAndResolve('');
    }
    wrap.addEventListener('click', (e) => {
      if (e.target === wrap) closeAndResolve('');
    });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(wrap);
    wrap.querySelector('#mlv-tag-cancel')?.addEventListener('click', () => closeAndResolve(''));
    wrap.querySelector('#mlv-tag-ok')?.addEventListener('click', () => {
      const v = wrap.querySelector('#mlv-comprador-tag-input')?.value || '';
      closeAndResolve(v);
    });
    wrap.querySelector('#mlv-comprador-tag-input')?.focus();
  });
}

export async function resolverUidCompradorParaVenta(getFirebase, raw) {
  const t = String(raw || '').trim();
  if (!t) throw new Error('Ingresá el código que te pasó el comprador.');
  if (/^[a-zA-Z0-9]{20,128}$/.test(t)) return t;
  const { fns } = getFirebase();
  if (!fns) throw new Error('Functions no disponible.');
  const call = httpsCallable(fns, 'resolverCompradorPorCodigo');
  const res = await call({ codigo: t });
  const uid = res?.data?.uid;
  if (!uid) throw new Error('No encontramos ese código.');
  return String(uid).trim();
}

/**
 * Marca vendida una publicación del vendedor autenticado (misma lógica que «Administrar ventas»).
 */
export async function runCompletarVentaFlow({
  getFirebase,
  repo,
  uid,
  productId,
  sourceBtn = null,
  onSuccess,
  onError,
  trackOrigen = 'mis',
}) {
  const vendedorUid = String(uid || '').trim();
  const pid = String(productId || '').trim();
  const sesionUid = String(getFirebase()?.auth?.currentUser?.uid || '').trim();
  if (!vendedorUid || !pid || sesionUid !== vendedorUid) {
    throw new Error('No podés marcar esta publicación.');
  }
  if (sourceBtn) {
    sourceBtn.disabled = true;
    sourceBtn.setAttribute('aria-busy', 'true');
  }
  try {
    const compradorInput = await openCompradorTagModal();
    const codigo = String(compradorInput || '').trim();
    let compradorUid = null;
    if (codigo) {
      compradorUid = await resolverUidCompradorParaVenta(getFirebase, codigo);
    }
    const profileBefore = await repo.loadUserProfile(vendedorUid, { forceServer: true });
    const rBefore = repo.resumenNivelVendedor(profileBefore);
    const progBefore = progresoSiguienteNivel(rBefore.ventasEnVentana);
    await repo.marcarComoVendido(vendedorUid, pid, { compradorTag: compradorUid || null });
    const profileAfter = await repo.loadUserProfile(vendedorUid, { forceServer: true });
    const rAfter = repo.resumenNivelVendedor(profileAfter);
    const progAfter = progresoSiguienteNivel(rAfter.ventasEnVentana);
    mlvTrack(MLV_EVENTS.VENTA_MARCADA, {
      con_comprador_tag: Boolean(compradorUid),
      nivel: rAfter.nivelEfectivo || 1,
      origen: trackOrigen,
    });
    await showVentaLogradaCelebracion({
      ventasDespues: rAfter.ventasEnVentana,
      progAntes: progBefore,
      progDespues: progAfter,
    });
    await onSuccess?.();
  } catch (err) {
    if (onError) await onError(err);
    else throw err;
    if (sourceBtn && sourceBtn.isConnected) {
      sourceBtn.disabled = false;
      sourceBtn.removeAttribute('aria-busy');
    }
  }
}
